import { Link } from "react-router-dom";
import { dismissFinished, useFinishedJob, type FinishedJob, type JobKind } from "../api/jobs";

const NOUN: Record<JobKind, string> = { sync: "Sync", extract: "Extraction", index: "Indexing", transcribe: "Transcription" };

// How the last job of `kind` this browser started ended, with its task
// summary and a link to the run it produced.
export function JobNotice({ kind }: { kind: JobKind }) {
  const job = useFinishedJob(kind);
  if (!job) return null;
  const bad = job.status === "failed" || job.status === "revoked" || job.status === "unknown";
  const partial = job.status === "partial";
  const tone = bad
    ? "border-red-200 bg-red-50 text-red-800"
    : partial
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : "border-green-200 bg-green-50 text-green-800";
  return (
    <div role="status" className={`mb-4 flex items-start justify-between gap-4 rounded border px-4 py-2 text-sm ${tone}`}>
      <div>
        <span className="font-medium">{job.label}</span>{" "}
        {headline(job)}
        {job.runId !== null && (
          <> · <Link to={`/runs?tab=${kind}&run=${job.runId}`} className="underline">{NOUN[kind]} #{job.runId}</Link></>
        )}
        {job.error && <div className="mt-0.5 break-words">{job.error}</div>}
      </div>
      <button onClick={() => dismissFinished(kind)} className="shrink-0 opacity-60 hover:opacity-100" aria-label="Dismiss">✕</button>
    </div>
  );
}

function headline(job: FinishedJob) {
  if (job.status === "unknown") return "— outcome unknown.";
  if (job.status === "revoked") return "was cancelled.";
  if (job.status === "failed") return "failed.";
  const r = (job.result ?? {}) as Record<string, unknown>;
  const n = (key: string) => (typeof r[key] === "number" ? (r[key] as number) : 0);
  const verb = job.status === "partial" ? "finished with some failures" : "finished";
  if (!job.result) return `${verb}.`;
  switch (job.kind) {
    case "sync": {
      if (!("courses_seen" in r)) return `${verb}.`;
      const missing = Array.isArray(r.missing) ? (r.missing as number[]) : [];
      return `${verb}: ${n("courses_seen")} courses, ${n("created")} created, ${n("updated")} updated, ${n("deleted")} removed.${missing.length ? ` Not in Moodle: ${missing.join(", ")}.` : ""}`;
    }
    case "extract":
      return "documents" in r
        ? `${verb}: ${n("documents")} documents from ${n("modules_seen")} modules${n("failures") ? `, ${n("failures")} failed` : ""}.`
        : `${verb}.`;
    case "index":
      return "chunks_indexed" in r
        ? `${verb}: ${n("chunks_indexed")} chunks from ${n("documents_seen")} documents${n("failures") ? `, ${n("failures")} failed` : ""}.`
        : `${verb}.`;
    case "transcribe":
      return "assets_seen" in r
        ? `${verb}: ${n("completed")} of ${n("assets_seen")} files transcribed${n("failed") ? `, ${n("failed")} failed` : ""}.`
        : `${verb}.`;
  }
}
